import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { ConfirmationArea } from "../../components/confirmationArea/ConfirmationArea";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { ICriteria } from "../../interfaces/ICriteria";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IJudgmentType } from "../../interfaces/IJudgmentType";
import { IOption } from "../../interfaces/IOption";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";

export const Criteria: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut } = useAuth();

  const [criteriaId, setCriteriaId] = useState("");
  const [judgmentTypesOpions, setJudgmentTypesOptions] = useState<IOption[]>(
    [],
  );
  const [judgmentTypeOption, setJudgmentTypeOption] = useState<IOption | null>(
    null,
  );

  const [sequence, setSequence] = useState("");
  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const [maximum, setMaximum] = useState<string | null>("");
  const [isActive, setIsActive] = useState(true);
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const handleChangeIsActive = (event: ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
  };

  const judgmentTypeToOption = (judgmentTypes: IJudgmentType): IOption => {
    return {
      value: `${judgmentTypes.id}`,
      description: `${judgmentTypes.id} - ${judgmentTypes.name}`,
    };
  };

  const handleSetJudgmentType = async (value: string | null) => {
    if (value) {
      const selected = judgmentTypesOpions.find(judgmentTypes => {
        return judgmentTypes.value === value;
      });

      if (selected?.value) {
        setJudgmentTypeOption(selected);
        await handleRequestNextSequence(Number(selected.value));
      } else {
        setJudgmentTypeOption(null);
      }
    }
  };

  const handleRequestNextSequence = useCallback(
    async (judgmentTypeId: number) => {
      setLoading(true);
      await api
        .get(`/criteria/next-sequence?judgmentTypeId=${judgmentTypeId}`)
        .then(response => {
          const nextSequence: number = response.data;
          setSequence(nextSequence.toString());
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          } else if (error.status === 404) {
            setSequence("");
          } else {
            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [navigate, signOut, toastr],
  );

  const handleResquestJudgmentTypes = useCallback(async () => {
    setLoading(true);

    await api
      .get(
        `/judgment-types?currentPage=1&perPage=100&orderField=id&orderBy=id&orderDirection=asc`,
      )
      .then(async response => {
        const judgmentTypes: IJudgmentType[] = response.data.data;

        const judgmentTypesOptions = judgmentTypes.map(judgmentTypeToOption);
        setJudgmentTypesOptions(judgmentTypesOptions);

        const selected = judgmentTypesOptions.find(judgmentTypes => {
          return judgmentTypes.value === judgmentTypeOption?.value;
        });

        if (selected?.value) {
          setJudgmentTypeOption(selected);
          await handleRequestNextSequence(Number(selected.value));
        } else {
          setJudgmentTypeOption(judgmentTypesOptions[0]);
          await handleRequestNextSequence(
            Number(judgmentTypesOptions[0].value),
          );
        }
      })
      .catch(error => {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }

        toastr.error(
          error.response.data?.message || "Contate a equipe de suporte",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    judgmentTypeOption?.value,
    navigate,
    signOut,
    toastr,
    handleRequestNextSequence,
  ]);

  const handleListCriteria = useCallback(async () => {
    const id = location.pathname.replace("/criteria", "").replace("/", "");

    await handleResquestJudgmentTypes();

    if (id) {
      setCriteriaId(id);
      setLoading(true);

      await api
        .get(`/criteria/${id}`)
        .then(response => {
          const criteria: ICriteria = response.data;

          criteria.judgmentType &&
            setJudgmentTypeOption(judgmentTypeToOption(criteria.judgmentType));

          setName(criteria.name ? criteria.name : "");
          setDetails(criteria.details ? criteria.details : "");
          setMaximum(criteria.maximum ? `${criteria.maximum}` : null);
          setIsActive(!!criteria.isActive);

          setSequence(criteria.sequence ? criteria.sequence.toString() : "");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    navigate,
    signOut,
    toastr,
    location.pathname,
    handleResquestJudgmentTypes,
  ]);

  const handleDeleteCriteria = useCallback(async () => {
    const id = location.pathname.replace("/criteria", "").replace("/", "");

    if (id) {
      setLoading(true);

      await api
        .delete(`/criteria/${id}`)
        .then(() => {
          navigate("/criteria-list");
          toastr.success("Critério excluída com sucesso");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, location.pathname]);

  const handleSubmit = useCallback(async () => {
    try {
      setFormErrors({});

      const schema = Yup.object().shape({
        judgmentTypeId: Yup.number()
          .required("Tipo de julgamento é obrigatório")
          .moreThan(0, "Tipo de julgamento é obrigatório"),
        sequence: Yup.number()
          .typeError("Sequência deve ser um número")
          .moreThan(0, "Sequência deve ser maior que 0")
          .lessThan(100, "Sequência deve ser menor que 100")
          .required("Sequência é obrigatório"),
        name: Yup.string().required("Nome é obrigatório"),
        maximum: Yup.number()
          .typeError("Máximo deve ser um número")
          .moreThan(0, "Máximo deve ser maior que 0")
          .lessThan(100, "Máximo deve ser menor que 100")
          .required("Máximo é obrigatório"),
      });

      if (criteriaId) {
        const data: ICriteria = {
          judgmentTypeId: Number(judgmentTypeOption?.value),
          sequence: sequence ? Number(sequence) : 0,
          name,
          details,
          maximum: Number(maximum),
          isActive,
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .put(`/criteria/${criteriaId}`, data)
          .then(async () => {
            await handleListCriteria();
            toastr.success("Critério atualizado com sucesso");
            navigate("/criteria-list");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const data: ICriteria = {
          judgmentTypeId: Number(judgmentTypeOption?.value),
          sequence: sequence ? Number(sequence) : 0,
          name,
          details,
          maximum: Number(maximum),
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .post(`/criteria`, data)
          .then(() => {
            toastr.success("Critério cadastrado com sucesso");
            navigate("/criteria-list");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(
        error.response.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [
    judgmentTypeOption,
    criteriaId,
    sequence,
    name,
    details,
    maximum,
    isActive,
    navigate,
    signOut,
    toastr,
    handleListCriteria,
  ]);

  useEffect(() => {
    handleListCriteria();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        <h1>Critério</h1>
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
            <Grid item xs={12} md={6} sm={6}>
              <FormControl
                fullWidth
                size="small"
                error={!!formErrors.judgmentTypeId}
              >
                <InputLabel
                  id="select-filter"
                  error={!!formErrors.judgmentTypeId}
                  required
                >
                  Tipo de Julgamento
                </InputLabel>
                <Select
                  label="Tipo de Julgamento"
                  value={judgmentTypeOption?.value || ""}
                  onChange={e => handleSetJudgmentType(String(e.target.value))}
                  error={!!formErrors.judgmentTypeId}
                  required
                  autoFocus
                >
                  {judgmentTypesOpions.map(judgmentType => {
                    return (
                      <MenuItem
                        key={judgmentType.description}
                        value={judgmentType.value}
                      >
                        {judgmentType.description}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{formErrors.judgmentTypeId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <InputMask
                mask={"999"}
                value={sequence || ""}
                onChange={e => setSequence(e.target.value)}
                maskPlaceholder={null}
              >
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="Sequência"
                  required
                  helperText={formErrors.sequence}
                  error={!!formErrors.sequence}
                />
              </InputMask>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Nome"
                value={name}
                required
                onChange={e => setName(e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <InputMask
                mask={"99"}
                value={maximum || ""}
                onChange={e => setMaximum(e.target.value)}
                maskPlaceholder={null}
              >
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="Nota Máxima"
                  required
                  helperText={formErrors.maximum}
                  error={!!formErrors.maximum}
                />
              </InputMask>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Observação"
                value={details}
                onChange={e => setDetails(e.target.value)}
              />
            </Grid>

            {criteriaId && (
              <Grid item xs={12} md={6} sm={6}>
                <FormControlLabel
                  label="Ativo"
                  control={
                    <Checkbox
                      checked={isActive}
                      onChange={handleChangeIsActive}
                    />
                  }
                />
              </Grid>
            )}
          </Grid>
          <ButtonGroup justformobilie>
            <ButtonTheme
              onClick={() => navigate("/criteria-list")}
              variant="contained"
              color="inherit"
              disabled={loading}
            >
              Voltar
            </ButtonTheme>

            {criteriaId && (
              <ButtonTheme
                onClick={() => setDialogDeleteOpen(true)}
                variant="contained"
                color="error"
                disabled={loading}
              >
                Excluir
              </ButtonTheme>
            )}

            <ButtonTheme
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
            >
              Confirmar
            </ButtonTheme>
          </ButtonGroup>
        </Form>

        <ConfirmationArea
          dialogOpen={dialogDeleteOpen}
          id={criteriaId}
          title="Deseja realmente excluir o item?"
          message=""
          key={`dialog-${criteriaId}`}
          handleConfirmation={handleDeleteCriteria}
          deny={() => setDialogDeleteOpen(false)}
        />

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
